import { FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail } from 'lucide-react';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { getAntiBotChallenge, solveAntiBotChallenge } from '../lib/db-storage';

const API_URL = `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/contact`;

export default function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    try {
      const challenge = await getAntiBotChallenge('contact_form');
      const antiBotProof = await solveAntiBotChallenge(challenge);
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          antiBotToken: antiBotProof.token,
          antiBotNonce: antiBotProof.nonce,
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || 'Unable to submit the contact form.');
      }

      toast.success('Message sent. We will get back to you soon.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to submit the contact form.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen px-4 pb-12 pt-16 sm:pt-20">
      <div className="mx-auto max-w-3xl space-y-8">
        <StockHeroStrip
          stockKey="coupleOutdoor"
          alt="Couple spending time together — illustrative stock imagery"
        />
        <div className="text-center">
          <p className="text-sm font-medium text-violet-200">Contact TENSION</p>
          <h1 className="mt-2 text-4xl font-bold text-white">Get in touch</h1>
          <p className="mt-3 text-zinc-200">
            Have a question, partnership idea, or integration request? Send us a note and we will reply from
            <span className="font-medium text-white"> hello@tensionlabs.net</span>.
          </p>
        </div>

        <Card className="border border-neutral-200/90 bg-white/98 p-6 text-neutral-900 shadow-lg md:p-8 dark:border-neutral-600 dark:bg-neutral-50">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-name" className="text-neutral-800">
                  Name
                </Label>
                <Input
                  id="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="h-11 border-neutral-300 bg-white text-base text-neutral-900 placeholder:text-neutral-500 shadow-sm dark:bg-neutral-100 dark:text-neutral-950 dark:placeholder:text-neutral-600"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email" className="text-neutral-800">
                  Email
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-11 border-neutral-300 bg-white text-base text-neutral-900 placeholder:text-neutral-500 shadow-sm dark:bg-neutral-100 dark:text-neutral-950 dark:placeholder:text-neutral-600"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-subject" className="text-neutral-800">
                Subject
              </Label>
              <Input
                id="contact-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="How can we help?"
                required
                className="h-11 border-neutral-300 bg-white text-base text-neutral-900 placeholder:text-neutral-500 shadow-sm dark:bg-neutral-100 dark:text-neutral-950 dark:placeholder:text-neutral-600"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-message" className="text-neutral-800">
                Message
              </Label>
              <Textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you need..."
                rows={7}
                required
                className="min-h-[160px] resize-y border-neutral-300 bg-white text-base text-neutral-900 placeholder:text-neutral-500 dark:bg-neutral-100 dark:text-neutral-950 dark:placeholder:text-neutral-600"
              />
            </div>

            <p className="text-sm text-neutral-600 dark:text-neutral-700">
              Automated anti-bot verification runs in the background when you submit.
            </p>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700"
            >
              <Mail className="mr-2 h-4 w-4" />
              {loading ? 'Sending...' : 'Send message'}
            </Button>
          </form>
        </Card>

        <p className="mt-6 text-center text-sm text-zinc-300">
          You can also email us directly at <a className="font-medium text-violet-200 hover:text-white hover:underline" href="mailto:hello@tensionlabs.net">hello@tensionlabs.net</a>.
          <span className="mx-2 text-zinc-500">•</span>
          <Link to="/early-access" className="font-medium text-violet-200 hover:text-white hover:underline">Back to Early Access</Link>
        </p>
      </div>
    </div>
    </WarmMarketingBackdrop>
  );
}
